'use server'

import bcrypt from 'bcryptjs'
import mongoose from 'mongoose'
import { redirect } from 'next/navigation'

import { signIn, signOut } from '@/auth'
import ROUTES from '@/constants/routes'
import { Account, Role, User, UserRole } from '@/database'

import action from '../handlers/action'
import handleError from '../handlers/error'
import { NotFoundError } from '../http-errors'
import { SignInSchema, SignUpSchema } from '../validations'

export async function signUpWithCredentials(
    params: AuthCredentials
): Promise<ActionResponse> {
    const validationResult = await action({ params, schema: SignUpSchema })

    if (validationResult instanceof Error) {
        return handleError(validationResult) as ErrorResponse
    }

    const { name, username, email, password } = validationResult.params!

    const session = await mongoose.startSession()
    session.startTransaction()

    try {
        const existingUser = await User.findOne({ email }).session(session)

        if (existingUser) {
            throw new Error('User already exists')
        }

        const existingUsername = await User.findOne({ username }).session(
            session
        )

        if (existingUsername) {
            throw new Error('Username already exists')
        }

        const userRole = await Role.findOne({ name: 'user' }).session(session)

        if (!userRole) {
            throw new Error('User role not found')
        }

        const hashedPassword = await bcrypt.hash(password, 12)

        const roles = [userRole._id]

        const [newUser] = await User.create(
            [{ username, name, email, roles }],
            {
                session,
            }
        )

        await UserRole.create(
            [
                {
                    user: newUser._id,
                    role: userRole._id,
                },
            ],
            { session }
        )

        await Account.create(
            [
                {
                    userId: newUser._id,
                    name,
                    provider: 'credentials',
                    providerAccountId: email,
                    password: hashedPassword,
                },
            ],
            { session }
        )

        await session.commitTransaction()

        await signIn('credentials', { email, password, redirect: false })

        return { success: true }
    } catch (error) {
        await session.abortTransaction()

        return handleError(error) as ErrorResponse
    } finally {
        await session.endSession()
    }
}

export async function signInWithCredentials(
    params: Pick<AuthCredentials, 'email' | 'password'>
): Promise<ActionResponse> {
    const validationResult = await action({ params, schema: SignInSchema })

    if (validationResult instanceof Error) {
        return handleError(validationResult) as ErrorResponse
    }

    const { email, password } = validationResult.params!

    try {
        const existingUser = await User.findOne({ email })

        if (!existingUser) throw new NotFoundError('User')

        const existingAccount = await Account.findOne({
            provider: 'credentials',
            providerAccountId: email,
        })

        if (!existingAccount) throw new NotFoundError('Account')

        const passwordMatch = await bcrypt.compare(
            password,
            existingAccount.password!
        )

        if (!passwordMatch) throw new Error('Password does not match')

        await signIn('credentials', { email, password, redirect: false })

        return { success: true }
    } catch (error) {
        return handleError(error) as ErrorResponse
    }
}

export async function signOutUser(): Promise<ActionResponse> {
    try {
        await signOut({ redirect: false })
    } catch (error) {
        return handleError(error) as ErrorResponse
    }

    redirect(ROUTES.HOME)
}
